// systems/bank.js
// Bank deposit/withdraw logic

import { gameState } from '../game/gameState.js';
import logger from '../utils/logger.js';

/**
 * Deposit item from inventory to bank
 * @param {string} itemId - Item to deposit
 * @param {number} quantity - Amount to deposit (default: all)
 * @returns {{ success: boolean, deposited: number, reason?: string }}
 */
export function depositItem(itemId, quantity = null) {
    const inventoryAmount = gameState.getItemCount(itemId);

    if (inventoryAmount === 0) {
        return { success: false, deposited: 0, reason: 'Item not in inventory' };
    }

    // If no quantity specified, deposit all
    const toDeposit = quantity === null ? inventoryAmount : Math.min(quantity, inventoryAmount);

    // Try to add to bank
    const bankResult = gameState.addBankItem(itemId, toDeposit);

    if (!bankResult.success) {
        return { success: false, deposited: 0, reason: bankResult.reason };
    }

    // Remove from inventory (use the amount actually added to bank)
    gameState.removeItem(itemId, bankResult.added);

    logger.debug(`[BANK] Deposited ${bankResult.added}x ${itemId}`);

    return { success: true, deposited: bankResult.added };
}

/**
 * Withdraw item from bank to inventory
 * @param {string} itemId - Item to withdraw
 * @param {number} quantity - Amount to withdraw (default: all)
 * @returns {{ success: boolean, withdrawn: number, reason?: string }}
 */
export function withdrawItem(itemId, quantity = null) {
    const bankAmount = gameState.getBankItemCount(itemId);

    if (bankAmount === 0) {
        return { success: false, withdrawn: 0, reason: 'Item not in bank' };
    }

    // If no quantity specified, withdraw all
    const toWithdraw = quantity === null ? bankAmount : Math.min(quantity, bankAmount);

    // Try to add to inventory
    const invResult = gameState.addItem(itemId, toWithdraw);

    if (!invResult.success) {
        return { success: false, withdrawn: 0, reason: invResult.reason };
    }

    // Remove from bank (use the amount actually added to inventory)
    gameState.removeBankItem(itemId, invResult.added);

    logger.debug(`[BANK] Withdrew ${invResult.added}x ${itemId}`);

    return { success: true, withdrawn: invResult.added };
}

/**
 * Deposit all inventory items to bank (skips locked items)
 * @returns {{ success: boolean, deposited: object, failed: object, skippedLocked: number }}
 */
export function depositAll() {
    const deposited = {};
    const failed = {};
    let skippedLocked = 0;

    // Get all inventory items from slots
    const inventorySlots = gameState.getInventorySlots();
    const inventoryItems = inventorySlots
        .filter(slot => {
            if (slot === null) return false;
            // Skip locked items
            if (gameState.isItemLocked(slot.itemId)) {
                skippedLocked++;
                return false;
            }
            return true;
        })
        .map(slot => ({ itemId: slot.itemId, quantity: slot.quantity }));

    for (const { itemId, quantity } of inventoryItems) {
        const result = depositItem(itemId, quantity);

        if (result.deposited > 0) {
            deposited[itemId] = result.deposited;
        }

        if (result.deposited < quantity) {
            failed[itemId] = quantity - result.deposited;
        }
    }

    const totalDeposited = Object.values(deposited).reduce((sum, q) => sum + q, 0);
    logger.debug(`[BANK] Deposited all: ${totalDeposited} items (${skippedLocked} locked items skipped)`);

    return {
        success: Object.keys(failed).length === 0,
        deposited,
        failed,
        skippedLocked
    };
}

/**
 * Get bank contents for UI rendering
 * @param {string} [sortBy='name'] - Sort by: 'name', 'quantity', 'category'
 * @param {string} [filter=''] - Text filter for item names
 * @returns {Array<{ itemId: string, quantity: number }>}
 */
export function getBankContents(sortBy = 'name', filter = '') {
    let contents = gameState.getBankSlots();

    // Apply filter
    if (filter) {
        const lowerFilter = filter.toLowerCase();
        contents = contents.filter(item =>
            item.itemId.toLowerCase().includes(lowerFilter)
        );
    }

    // Apply sorting
    switch (sortBy) {
        case 'quantity':
            contents.sort((a, b) => b.quantity - a.quantity);
            break;
        case 'name':
        default:
            contents.sort((a, b) => a.itemId.localeCompare(b.itemId));
            break;
    }

    return contents;
}

/**
 * Get bank statistics
 * @returns {{ usedSlots: number, maxSlots: number, totalItems: number, bonusSlots: number }}
 */
export function getBankStats() {
    const slots = gameState.getBankSlots();
    const maxSlots = gameState.getBankMaxSlots();
    const bonusSlots = gameState.getBankStorageBonus();
    return {
        usedSlots: slots.length,
        maxSlots,
        totalItems: slots.reduce((sum, slot) => sum + slot.quantity, 0),
        bonusSlots
    };
}

export default {
    depositItem,
    withdrawItem,
    depositAll,
    getBankContents,
    getBankStats
};
